import { ListItemIcon, ListItemText } from '@mui/material';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import DotsIcon from 'assets/v2/DotsIcon';

import {
  KebabMenuContextMenu,
  KebabMenuContextMenuItem,
  KebabMenuControlButton,
} from './KebabMenu.styles';

/**
 * KebabMenu component
 *
 * @param {Object} props - The component props.
 * @param {Array<Object>} props.items - The context menu items.
 * @param {Object} props.data - Any data that is needed as a parameter for determining the disabled state or onClick handler of the context menu items.
 * @param {Boolean} props.disabled - Whether the control button is disabled.
 * @param {Object} props.anchorOrigin - The anchor origin for the context menu. Defaults to `{ vertical: 'bottom', horizontal: 'right' }`.
 * @param {Object} props.transformOrigin - The transform origin for the context menu. Defaults to `{ vertical: 'bottom', horizontal: 'left' }`.
 * @param {React.Component} props.ControlButtonIconComponent - The icon component for the control button of the context menu.
 * @param {Object} props.controlButtonIconProps - The props for the control button icon. Defaults to `{ size: '24px' }`.
 * @param {Boolean} props.stopPropagation - Whether to stop propagation for the context menu. Defaults to `true`.
 * @param {Object} props.contextMenuListProps - The menu list props for the context menu. Defaults to `{}`.
 * @param {String} props.controlButtonUuid - The UUID for the control button. Defaults to `kebab-menu-button-<random-uuid>`.
 */
const KebabMenu = ({
  items = [],
  data,
  disabled,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'bottom', horizontal: 'left' },
  ControlButtonIconComponent = DotsIcon,
  controlButtonIconProps = { size: '20px' },
  stopPropagation = true,
  contextMenuListProps,
  controlButtonUuid = `kebab-menu-button-${crypto.randomUUID()}`,
}) => {
  const contextMenuUuid = `${controlButtonUuid}-menu-${crypto.randomUUID()}`;

  const [anchorEl, setAnchorEl] = useState(null);

  const isOpen = Boolean(anchorEl);

  const controlButtonAriaControls = isOpen ? contextMenuUuid : undefined;
  const controlButtonAriaExpanded = isOpen ? 'true' : undefined;

  const handleOpen = useCallback(
    (event) => {
      if (stopPropagation) event.stopPropagation();

      setAnchorEl(event.currentTarget);
    },
    [stopPropagation],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleActionClick = useCallback(
    (onClick) => {
      if (onClick) onClick(data);

      handleClose();
    },
    [data, handleClose],
  );

  const renderMenuItemIcon = useCallback((Icon) => {
    if (!Icon) return null;

    return React.isValidElement(Icon) ? Icon : <Icon />;
  }, []);

  const contextMenuItems = useMemo(() => {
    // Identify if any context menu items have icons to determine how to align menu item labels without icons (if any)
    const hasIcons = items.some((item) => Boolean(item.icon));

    return items.map(
      ({ isDisabled, icon, onClick, label, to, href, target, rel }) => {
        const isItemDisabled = isDisabled?.(data);

        const Icon = icon;

        const commonProps = {
          key: crypto.randomUUID(),
          onClick: () => handleActionClick(onClick),
          disabled: isItemDisabled,
        };

        // Properties specific to context menu item components that are links
        const linkProps = {
          ...((to || href) && { target, rel }), // sets target and rel attributes if either `to` or `href` is provided
          ...(to && { component: Link, to }), // renders React Router `Link` component if `to` is provided
          ...(href && { component: 'a', href }), // renders native HTML anchor element if `href` is provided
        };

        return (
          <KebabMenuContextMenuItem {...commonProps} {...linkProps}>
            {hasIcons && (
              <ListItemIcon>{renderMenuItemIcon(Icon)}</ListItemIcon>
            )}
            <ListItemText>{label}</ListItemText>
          </KebabMenuContextMenuItem>
        );
      },
    );
  }, [items, data, renderMenuItemIcon, handleActionClick]);

  if (!items?.length) return null;

  return (
    <>
      <KebabMenuControlButton
        id={controlButtonUuid}
        aria-controls={controlButtonAriaControls}
        aria-haspopup="true"
        aria-expanded={controlButtonAriaExpanded}
        onClick={handleOpen}
        disabled={disabled}
      >
        <ControlButtonIconComponent {...controlButtonIconProps} />
      </KebabMenuControlButton>
      <KebabMenuContextMenu
        id={contextMenuUuid}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        MenuListProps={{ 'aria-label': 'Menu', ...contextMenuListProps }}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        {contextMenuItems}
      </KebabMenuContextMenu>
    </>
  );
};

KebabMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
      onClick: PropTypes.func,
      isDisabled: PropTypes.func,
      to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      href: PropTypes.string,
      target: PropTypes.string,
      rel: PropTypes.string,
    }),
  ),
  data: PropTypes.any,
  disabled: PropTypes.bool,
  anchorOrigin: PropTypes.object,
  transformOrigin: PropTypes.object,
  ControlButtonIconComponent: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node,
  ]),
  controlButtonIconProps: PropTypes.object,
  stopPropagation: PropTypes.bool,
  contextMenuListProps: PropTypes.object,
  controlButtonUuid: PropTypes.string,
};

export default memo(KebabMenu);
